import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';

/**
 * SAVR brand theme, derived from the frontier design system:
 *  - font: Söhne (aliased "savr-sans"), embedded from /fonts
 *  - brand: purple  (dark hsl(238,100%,78%) -> #8F93FF, light hsl(241,56%,55%) -> #4E4CCD)
 *  - dark background lifted to #0B0B0E (near-black read as too dark); light -> #fff
 *  - dark sidebar in both themes, brand-purple selection indicator
 *
 * The component overrides carry the sign-in page's visual language through the
 * whole app: near-black canvas with a faint purple glow, hairline-bordered
 * glass surfaces, soft radii, and calm (non-uppercase) controls.
 */

const fontFamily =
  '"savr-sans", "Söhne", "Helvetica Neue", Helvetica, Arial, sans-serif';

const fontFaces = `
@font-face {
  font-family: 'savr-sans';
  src: url('/fonts/soehne-buch.woff2') format('woff2');
  font-weight: 400; font-style: normal; font-display: swap;
}
@font-face {
  font-family: 'savr-sans';
  src: url('/fonts/soehne-buch-kursiv.woff2') format('woff2');
  font-weight: 400; font-style: italic; font-display: swap;
}
@font-face {
  font-family: 'savr-sans';
  src: url('/fonts/soehne-kraftig.woff2') format('woff2');
  font-weight: 500; font-style: normal; font-display: swap;
}
@font-face {
  font-family: 'savr-sans';
  src: url('/fonts/soehne-halbfett.woff2') format('woff2');
  font-weight: 700; font-style: normal; font-display: swap;
}
`;

// Homepage widgets live in fixed-height react-grid-layout cells. The grid
// sets overflow:auto on the card content but never bounds its height, so
// long widgets (Starred entities, Recently visited, ...) clip instead of
// scrolling. Flexing the card gives the content a real height to scroll in.
const homepageWidgetScroll = `
.react-grid-item div[class*="MuiCard-root"] {
  display: flex;
  flex-direction: column;
}
.react-grid-item div[class*="MuiCardContent-root"] {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
`;

// Faint fixed purple glow behind everything + slim scrollbars (dark).
const darkGlobals = `
body {
  background-color: #0B0B0E;
  background-image:
    radial-gradient(ellipse 55% 40% at 70% -10%, rgba(143, 147, 255, 0.10), transparent),
    radial-gradient(ellipse 45% 35% at 0% 100%, rgba(46, 43, 128, 0.18), transparent);
  background-attachment: fixed;
}
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.14); border-radius: 8px; }
*::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.24); }
/* The @backstage/ui (BUI) cards - About, Labels, ... - paint their own grey
   token surfaces; align them with the MUI paper glass (#101013). The theme
   provider stamps data-theme-mode on <body>; body[...] outranks BUI's bare
   [data-theme-mode] token block. */
body[data-theme-mode='dark'] {
  --bui-bg-app: #0B0B0E;
  --bui-bg-neutral-1: #101013;
  --bui-bg-neutral-2: #17171b;
  --bui-bg-neutral-3: #1e1e23;
  --bui-bg-neutral-4: #26262c;
  --bui-border-1: rgba(255, 255, 255, 0.07);
  --bui-border-2: rgba(255, 255, 255, 0.14);
}
`;

const lightGlobals = `
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(17, 17, 26, 0.18); border-radius: 8px; }
*::-webkit-scrollbar-thumb:hover { background: rgba(17, 17, 26, 0.32); }
`;

// Phone-width fixes (the desktop layout is otherwise already responsive:
// cards stack, the sidebar becomes a bottom bar, tabs scroll).
const mobileGlobals = `
@media (max-width: 600px) {
  /* Wide BUI tables (relation cards: Has subcomponents, Provided APIs, ...)
     crush every column to an unreadable sliver. Their scroll container is
     already overflow-x:auto - a min-width makes them scroll instead. Only
     tables with 4+ columns; two-column ones (Labels) fit fine. */
  table[class*='bui-Table']:has(thead th:nth-child(4)) { min-width: 560px; }
  /* The catalog/API-explorer table uses percentage column widths, which
     crush at phone width; the scroll container already handles overflow.
     MuiTable-root is one of the few class names that stays stable in
     production builds (non-Mui JSS names minify to jss4-N). */
  table[class*='MuiTable-root'] { min-width: 760px; }
}
`;

/**
 * Global CSS per theme, rendered as a <style> tag by the theme providers in
 * App.tsx. MuiCssBaseline.styleOverrides does NOT work here: the unified
 * theme's v4 adapter silently drops string overrides, and neither the new
 * frontend system nor UnifiedThemeProvider renders a CssBaseline - so
 * anything put there never reaches the page.
 */
export const savrDarkGlobalCss =
  fontFaces + homepageWidgetScroll + mobileGlobals + darkGlobals;
export const savrLightGlobalCss =
  fontFaces + homepageWidgetScroll + mobileGlobals + lightGlobals;

const navigation = {
  background: '#0D0D0F',
  indicator: '#8F93FF',
  color: 'rgba(255, 255, 255, 0.77)',
  selectedColor: '#ffffff',
  navItem: { hoverBackground: 'rgba(143, 147, 255, 0.12)' },
  submenu: { background: '#0D0D0F' },
};

// SAVR purple gradients for the page header banners (replaces the stock
// green/teal gradients on entity and home pages). In the dark theme the
// BackstageHeader override below swaps the banner for dark glass + glow;
// genPageTheme still supplies the white font color.
const pageColors = ['#2E2B80', '#8F93FF'];
const makePageTheme = (colors: string[]) => ({
  home: genPageTheme({ colors, shape: shapes.wave }),
  documentation: genPageTheme({ colors, shape: shapes.wave2 }),
  tool: genPageTheme({ colors, shape: shapes.round }),
  service: genPageTheme({ colors, shape: shapes.wave }),
  website: genPageTheme({ colors, shape: shapes.wave }),
  library: genPageTheme({ colors, shape: shapes.wave }),
  other: genPageTheme({ colors, shape: shapes.wave }),
  app: genPageTheme({ colors, shape: shapes.wave }),
  apis: genPageTheme({ colors, shape: shapes.wave }),
  card: genPageTheme({ colors, shape: shapes.wave }),
});
const pageTheme = makePageTheme(pageColors);
// In the dark theme the page headers use the glass override below, so
// pageTheme gradients only surface in small tiles (e.g. the group pages'
// Ownership card). Bright waves clash there - use calm dark navy-purple.
const darkPageTheme = makePageTheme(['#15152A', '#26235A']);

// Group/user Ownership tiles (dark theme): the stock pageTheme wave
// gradients read as washed-out grey against the dark glass language -
// repaint as glass with a soft purple bloom. The static override replaces
// the plugin's props-based background rule. Kept in its own const because
// custom (non-Mui) component keys fail TS's excess-property check when
// written inline in createUnifiedTheme's components literal.
const darkOwnershipTiles = {
  PluginOrgComponentsGrid: {
    styleOverrides: {
      card: {
        border: '1px solid rgba(255, 255, 255, 0.09)',
        borderRadius: 14,
        boxShadow: 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          borderColor: 'rgba(143, 147, 255, 0.5)',
          boxShadow: '0 0 16px rgba(143, 147, 255, 0.18)',
        },
      },
      entityTypeBox: {
        background: [
          'radial-gradient(ellipse 90% 130% at 12% -25%, rgba(143, 147, 255, 0.18), transparent)',
          'radial-gradient(ellipse 70% 110% at 100% 130%, rgba(46, 43, 128, 0.28), transparent)',
          'linear-gradient(180deg, #15151c 0%, #0F0F14 100%)',
        ].join(', '),
        color: '#ffffff',
        '& [class*="MuiTypography-subtitle1"]': {
          color: 'rgba(255, 255, 255, 0.66)',
        },
      },
    },
  },
};

// Entity titles at phone width: the title text sits in nested inline-flex
// boxes that never shrink or wrap, clipping behind the header edge. Inline
// display restores normal text wrapping. Applied via theme overrides (NOT
// global CSS selectors) because production JSS minifies non-Mui class names
// to jss4-N, so [class*='BackstageHeader-title'] only matches in dev.
const headerTitleMobile = {
  '@media (max-width: 600px)': {
    fontSize: 26,
    '& [class*="MuiBox"]': {
      display: 'inline',
      maxWidth: '100%',
      whiteSpace: 'normal' as const,
    },
    '& *': { minWidth: 0, overflowWrap: 'anywhere' as const },
  },
};

// UI physics shared by both themes: radii, weights, quiet controls.
const sharedComponents = {
  // See headerTitleMobile. The dark theme replaces this whole BackstageHeader
  // entry with its glass header and must re-include the title rule.
  BackstageHeader: {
    styleOverrides: {
      title: headerTitleMobile,
    },
  },
  // Catalog table toolbar at phone width: the title and search field render
  // on top of each other - stack them instead.
  PluginCatalogTableToolbar: {
    styleOverrides: {
      root: {
        '@media (max-width: 600px)': { flexWrap: 'wrap' as const },
      },
    },
  },
  CatalogReactEntitySearchBar: {
    styleOverrides: {
      searchToolbar: {
        '@media (max-width: 600px)': {
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        textTransform: 'none' as const,
        fontWeight: 500,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 500,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      // Tighter margins than the Backstage default so wrapped tag rows
      // (catalog table, About card) don't balloon vertically.
      root: { borderRadius: 8, marginRight: 6, marginBottom: 4 },
    },
  },
  // About-card icon links ("View TechDocs", "View Source"): the row sizes
  // columns to min-content, which wraps every label onto two cramped lines.
  // Nowrap lets each link take its natural width.
  BackstageIconLinkVertical: {
    styleOverrides: {
      label: { whiteSpace: 'nowrap' as const },
    },
  },
  // ...and center the row within the card.
  BackstageHeaderIconLinkRow: {
    styleOverrides: {
      links: { justifyContent: 'center' as const, width: '100%' },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16 },
    },
  },
  // Calm, non-uppercase table headers matching the rest of the controls.
  // nowrap keeps titles on one line in narrow (user-resized) columns, and the
  // column-resize handle is shrunk with a little gap so it never sits flush
  // against the next column's title.
  BackstageTableHeader: {
    styleOverrides: {
      header: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: 13,
        borderTop: 'none',
        whiteSpace: 'nowrap' as const,
        // Align header text with body cell text (both 16px inset).
        padding: '8px 16px',
        // Never let a resized-to-nothing column overlap its neighbor: keep a
        // grabbable minimum and clip instead of bleeding.
        minWidth: 48,
        overflow: 'hidden' as const,
        '& .MuiIcon-root': {
          fontSize: 16,
          marginLeft: 6,
        },
      },
    },
  },
};

export const savrDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#8F93FF' },
    secondary: { main: '#A5A4FF' },
    error: { main: '#FF385F' },
    background: { default: '#0B0B0E', paper: '#101013' },
    navigation,
  },
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme: darkPageTheme,
  components: {
    ...sharedComponents,
    // Glass surfaces: hairline border, no MUI gradient overlay.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#101013',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.07)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },
    // Page banners: dark glass with a purple bloom instead of the gradient wave.
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: [
            'radial-gradient(ellipse 60% 160% at 12% -30%, rgba(143, 147, 255, 0.22), transparent)',
            'radial-gradient(ellipse 45% 140% at 95% 130%, rgba(46, 43, 128, 0.35), transparent)',
            'linear-gradient(180deg, #0C0C13 0%, #09090E 100%)',
          ].join(', '),
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
        // Re-included: this entry replaces sharedComponents.BackstageHeader.
        title: headerTitleMobile,
      },
    },
    ...darkOwnershipTiles,
    BackstageSidebar: {
      styleOverrides: {
        drawer: { borderRight: '1px solid rgba(255, 255, 255, 0.06)' },
      },
    },
    // Soft rows: hairline dividers and a brand-tinted hover instead of the
    // harsh black zebra striping the stock dark palette produces.
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: 'rgba(143, 147, 255, 0.06)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          minWidth: 48,
          overflow: 'hidden',
        },
      },
    },
  },
});

export const savrLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#4E4CCD' },
    secondary: { main: '#4E4CCD' },
    error: { main: '#E11D48' },
    background: { default: '#FFFFFF', paper: '#FFFFFF' },
    navigation,
  },
  fontFamily,
  defaultPageTheme: 'home',
  pageTheme,
  components: {
    ...sharedComponents,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(17, 17, 26, 0.09)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          boxShadow: 'none',
        },
        // Re-included: this entry replaces sharedComponents.BackstageHeader.
        title: headerTitleMobile,
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: { borderRight: '1px solid rgba(17, 17, 26, 0.08)' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: 'rgba(78, 76, 205, 0.05)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(17, 17, 26, 0.07)',
          minWidth: 48,
          overflow: 'hidden',
        },
      },
    },
  },
});
